/**
 * Shared balance state for the top-bar credits pill + the global
 * low-balance banner. One fetch per route change instead of N — and
 * both consumers stay in lockstep (they'd otherwise be able to disagree
 * during the brief window between the pill's fetch and the banner's).
 *
 * Deliberately minimal: exposes balance + threshold + derived flags only.
 * Deeper billing data (rate card, volume brackets, ledger) stays on
 * `/billing`'s own state — no drive-by refactor.
 */
import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import api from '@/services/apiClient';

interface BalanceSnapshot {
  balanceCents: number;
  lowBalanceThresholdCents: number;
}

interface BalanceContextValue {
  balance: BalanceSnapshot | null;
  isLow: boolean;
  /** Refetch on demand — call after any action that moves balance (manual
   *  top-up success, admin adjustment from a different tab, etc.). */
  refetch: () => void;
}

const BalanceContext = createContext<BalanceContextValue | null>(null);

export function BalanceProvider({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  const [balance, setBalance] = useState<BalanceSnapshot | null>(null);
  const [nonce, setNonce] = useState(0);

  const refetch = useCallback(() => setNonce((n) => n + 1), []);

  useEffect(() => {
    const ctrl = new AbortController();
    (async () => {
      try {
        const data = await api.billing.balance(ctrl.signal);
        setBalance({
          balanceCents: data.balanceCents,
          lowBalanceThresholdCents: data.lowBalanceThresholdCents,
        });
      } catch {
        // Aborted, 401 pre-auth, transient — all render nothing. A
        // broken balance fetch must never block the rest of the UI.
      }
    })();
    return () => ctrl.abort();
    // Refetch on route change so the value tracks every action that
    // might move balance (manual top-up, call debit, admin adjustment).
  }, [pathname, nonce]);

  const isLow = balance !== null && balance.balanceCents < balance.lowBalanceThresholdCents;

  return (
    <BalanceContext.Provider value={{ balance, isLow, refetch }}>
      {children}
    </BalanceContext.Provider>
  );
}

export function useBalance(): BalanceContextValue {
  const ctx = useContext(BalanceContext);
  if (!ctx) throw new Error('useBalance must be used within <BalanceProvider>');
  return ctx;
}
